/**
 * Llojet e punës (WorkingType) — fjalori qendror i klasifikimit të orës.
 *
 * Admini shton/ndryshon tipe pa kod: kodi, emri, kategoria, i paguar, ndikimi
 * te paga / prezenca / pushimi, orë shtesë, ngjyra, renditja. Tipet e sistemit
 * mbajnë kodin dhe kategorinë të ngrira (moduli harton mbi to) dhe nuk fshihen,
 * vetëm çaktivizohen. Rregullat i zbaton serveri (services/workingTypes.js).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack, Typography, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Switch, FormControlLabel, Snackbar, Tooltip, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import { authFetch } from '../../services/apiServices';

export const CATEGORY_LABEL = { WORK: 'Punë', MEETING: 'Takim', TRAINING: 'Trajnim', BREAK: 'Pauzë', LEAVE: 'Pushim', HOLIDAY: 'Festë', OVERTIME: 'Orë shtesë', ABSENCE: 'Mungesë', INACTIVE: 'Joaktiv' };
export const PAYROLL_LABEL = { PAID_REGULAR: 'Paguhet normalisht', PAID_PREMIUM: 'Paguhet me shtesë', UNPAID: 'Nuk paguhet', ACCRUE: 'Grumbullohet (bankë)', NONE: 'Pa ndikim' };
export const ATTENDANCE_LABEL = { PRESENT: 'Prezencë', EXCUSED: 'E justifikuar', ABSENT: 'Mungesë', NEUTRAL: 'Neutral' };
export const LEAVE_LABEL = { NONE: 'Pa ndikim', DEDUCT_ANNUAL: 'Zbret pushimin vjetor', DEDUCT_TYPE: 'Zbret balancën e llojit', NO_DEDUCT: 'Nuk zbret' };

const BLANK = { code: '', name: '', category: 'WORK', isPaid: true, payrollImpact: 'PAID_REGULAR', attendanceImpact: 'PRESENT', leaveImpact: 'NONE', isOvertime: false, isActive: true, color: '#64748B', sortOrder: 500, description: '' };

export default function WorkingTypesTab() {
  const [types, setTypes] = useState([]);
  const [enums, setEnums] = useState({ CATEGORIES: [], PAYROLL_IMPACTS: [], ATTENDANCE_IMPACTS: [], LEAVE_IMPACTS: [] });
  const [edit, setEdit] = useState(null);
  const [snack, setSnack] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/working-types?all=1');
      const j = await r.json();
      if (j?.success) { setTypes(j.data.types || []); setEnums(j.data.enums || enums); }
    } catch (_) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      const isNew = !edit.id;
      const r = await authFetch(isNew ? '/api/working-types' : `/api/working-types/${edit.id}`, {
        method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(edit),
      });
      let j = await r.json().catch(() => ({}));
      // Histori financiare e mbyllur: serveri kërkon pranim të shprehur; muajt e
      // mbyllur mbajnë përkufizimin e vjetër (snapshot), ndryshimi vlen nga sot.
      if (r.status === 409 && j?.code === 'CLOSED_HISTORY') {
        const months = (j.closedMonths || []).join(', ');
        if (!window.confirm(`Tipi ${edit.code} përdoret në muaj me listë page të mbyllur (${months}).\nMuajt e mbyllur ruajnë përkufizimin e vjetër; ndryshimi vlen për periudhat e hapura.\nVazhdon?`)) return;
        const r2 = await authFetch(`/api/working-types/${edit.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...edit, acknowledgeClosedHistory: true }) });
        j = await r2.json().catch(() => ({}));
        if (!r2.ok || !j?.success) { setSnack(j?.error || `Gabim ${r2.status}`); return; }
        setSnack(`Tipi ${j.data.code} u ruajt (histori e mbyllur: ${months}).`); setEdit(null); load(); return;
      }
      if (!r.ok || !j?.success) { setSnack(j?.error || `Gabim ${r.status}`); return; }
      setSnack(isNew ? `Tipi ${j.data.code} u shtua.` : `Tipi ${j.data.code} u ruajt.`);
      setEdit(null); load();
    } finally { setBusy(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Fshi tipin ${t.code}?`)) return;
    const r = await authFetch(`/api/working-types/${t.id}`, { method: 'DELETE' });
    const j = await r.json().catch(() => ({}));
    setSnack(r.ok && j?.success ? 'U fshi.' : (j?.error || `Gabim ${r.status}`));
    load();
  };

  const groups = useMemo(() => {
    const g = { paid: [], unpaid: [] };
    for (const t of types) (t.isPaid ? g.paid : g.unpaid).push(t);
    return g;
  }, [types]);

  const Row = ({ t }) => (
    <TableRow hover sx={{ opacity: t.isActive ? 1 : 0.55 }}>
      <TableCell><Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: t.color, display: 'inline-block', mr: 1, verticalAlign: 'middle' }} /><code style={{ fontSize: 12 }}>{t.code}</code></TableCell>
      <TableCell><Typography variant="body2" fontWeight={600}>{t.name}</Typography>{t.description && <Typography variant="caption" color="text.secondary">{t.description}</Typography>}</TableCell>
      <TableCell><Chip size="small" label={CATEGORY_LABEL[t.category] || t.category} variant="outlined" /></TableCell>
      <TableCell><Typography variant="caption">{PAYROLL_LABEL[t.payrollImpact] || t.payrollImpact}</Typography></TableCell>
      <TableCell><Typography variant="caption">{ATTENDANCE_LABEL[t.attendanceImpact] || t.attendanceImpact}</Typography></TableCell>
      <TableCell><Typography variant="caption">{LEAVE_LABEL[t.leaveImpact] || t.leaveImpact}</Typography></TableCell>
      <TableCell align="center">{t.isOvertime ? '✓' : '—'}</TableCell>
      <TableCell align="center">{t.isSystem ? <Tooltip title="Tip i sistemit: kodi dhe kategoria s'ndryshohen, nuk fshihet"><LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></Tooltip> : ''}</TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <IconButton size="small" onClick={() => setEdit({ ...t })} data-testid={`wt-edit-${t.code}`}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
        {!t.isSystem && <IconButton size="small" color="error" onClick={() => remove(t)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>}
      </TableCell>
    </TableRow>
  );

  const Group = ({ title, list }) => (
    <>
      <TableRow><TableCell colSpan={9} sx={{ bgcolor: 'grey.50', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.secondary' }}>{title} · {list.length}</TableCell></TableRow>
      {list.map((t) => <Row key={t.code} t={t} />)}
    </>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>Llojet e punës (WorkingType)</Typography>
          <Typography variant="caption" color="text.secondary">Një fjalor për prezencën, pushimet, pagat, pauzat dhe trajnimet. Shto tipe të reja pa kod; ndikimet vendosin si numërohen orët.</Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setEdit({ ...BLANK })} sx={{ textTransform: 'none', borderRadius: 1.5 }} data-testid="wt-add">Shto tip</Button>
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12 } }}>
            <TableCell>Kodi</TableCell><TableCell>Emri</TableCell><TableCell>Kategoria</TableCell><TableCell>Paga</TableCell><TableCell>Prezenca</TableCell><TableCell>Pushimi</TableCell><TableCell align="center">Orë shtesë</TableCell><TableCell align="center">Sistem</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>
            <Group title="Të pagueshme" list={groups.paid} />
            <Group title="Të papagueshme" list={groups.unpaid} />
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{edit?.id ? `Ndrysho ${edit.code}` : 'Tip i ri pune'}</DialogTitle>
        <Divider />
        <DialogContent>
          {edit && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField size="small" label="Kodi" value={edit.code} disabled={edit.isSystem} onChange={(e) => setEdit((p) => ({ ...p, code: e.target.value.toUpperCase() }))} helperText="P.sh. EVL_HOURS — i qëndrueshëm, s'ndryshohet pas përdorimit" fullWidth inputProps={{ 'data-testid': 'wt-code' }} />
                <TextField size="small" label="Emri" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField select size="small" label="Kategoria" value={edit.category} disabled={edit.isSystem} onChange={(e) => setEdit((p) => ({ ...p, category: e.target.value }))} fullWidth>
                  {enums.CATEGORIES.map((c) => <MenuItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Renditja" value={edit.sortOrder} onChange={(e) => setEdit((p) => ({ ...p, sortOrder: Number(e.target.value) }))} fullWidth />
                <TextField size="small" type="color" label="Ngjyra" value={edit.color} onChange={(e) => setEdit((p) => ({ ...p, color: e.target.value }))} sx={{ minWidth: 90 }} InputLabelProps={{ shrink: true }} />
              </Stack>
              <FormControlLabel control={<Switch checked={edit.isPaid !== false} onChange={(e) => setEdit((p) => ({ ...p, isPaid: e.target.checked, payrollImpact: e.target.checked ? (p.payrollImpact === 'UNPAID' ? 'PAID_REGULAR' : p.payrollImpact) : (['PAID_REGULAR', 'PAID_PREMIUM'].includes(p.payrollImpact) ? 'UNPAID' : p.payrollImpact) }))} />} label="I paguar" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField select size="small" label="Ndikimi te paga" value={edit.payrollImpact} onChange={(e) => setEdit((p) => ({ ...p, payrollImpact: e.target.value }))} fullWidth>
                  {enums.PAYROLL_IMPACTS.map((c) => <MenuItem key={c} value={c}>{PAYROLL_LABEL[c] || c}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Ndikimi te prezenca" value={edit.attendanceImpact} onChange={(e) => setEdit((p) => ({ ...p, attendanceImpact: e.target.value }))} fullWidth>
                  {enums.ATTENDANCE_IMPACTS.map((c) => <MenuItem key={c} value={c}>{ATTENDANCE_LABEL[c] || c}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Ndikimi te pushimi" value={edit.leaveImpact} onChange={(e) => setEdit((p) => ({ ...p, leaveImpact: e.target.value }))} fullWidth>
                  {enums.LEAVE_IMPACTS.map((c) => <MenuItem key={c} value={c}>{LEAVE_LABEL[c] || c}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControlLabel control={<Switch checked={!!edit.isOvertime} onChange={(e) => setEdit((p) => ({ ...p, isOvertime: e.target.checked }))} />} label="Orë shtesë" />
                <FormControlLabel control={<Switch checked={edit.isActive !== false} onChange={(e) => setEdit((p) => ({ ...p, isActive: e.target.checked }))} />} label="Aktiv" />
              </Stack>
              <TextField size="small" label="Përshkrimi" value={edit.description || ''} onChange={(e) => setEdit((p) => ({ ...p, description: e.target.value }))} multiline minRows={2} fullWidth />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setEdit(null)} variant="outlined" sx={{ textTransform: 'none' }}>Anulo</Button>
          <Button onClick={save} variant="contained" disabled={busy} sx={{ textTransform: 'none' }} data-testid="wt-save">Ruaj</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
